/**
 * shared/components/AdminCommandPalette.jsx (v2.71.66)
 * ----------------------------------------------------
 * Linear/Notion/VSCode-style global search palette for admins.
 *
 *  ⌘K / Ctrl+K  → toggle (registered globally via AdminCommandPaletteProvider)
 *  ↑ / ↓        → navigate results
 *  Enter        → open selected result
 *  Esc          → close
 *  ⌘+E          → toggle "featured" on selected property (quick action)
 *
 * Results are GROUPED by entity type (Properties · Projects · Users ·
 * Leads). Each row has a compact preview, status badges, and a quick
 * "open" action. Property rows expose verify + mark-featured; user
 * rows expose suspend/unsuspend. All quick actions hit existing admin
 * endpoints and never leave the palette.
 *
 * SECURITY: this component is mounted ONLY when `user?.role === "admin"`.
 * The backend `/api/admin/global-search` is also `require_admin` gated,
 * so even if the component leaked, the API wouldn't.
 */

import React, {
  createContext, useCallback, useContext, useEffect, useMemo,
  useRef, useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, Home, Building2, User as UserIcon, MessageSquare,
  CornerDownLeft, ArrowUp, ArrowDown, Loader2, BadgeCheck, Star,
  ShieldCheck, ShieldOff, Sparkles, Tag, Layers, Crown, AlertTriangle,
  Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";

import api, { apiErrorMsg } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import * as VisuallyHidden from "@radix-ui/react-visually-hidden";

const ENTITY_ORDER = ["properties", "projects", "users", "leads"];
const ENTITY_META = {
  properties: { label: "Properties", icon: Home,        hint: "title · id · locality · phone · ₹price · BHK" },
  projects:   { label: "Projects",   icon: Building2,   hint: "name · locality · builder" },
  users:      { label: "Users",      icon: UserIcon,    hint: "name · email · phone" },
  leads:      { label: "Leads",      icon: MessageSquare, hint: "name · phone · lead id" },
};

const DEBOUNCE_MS = 180;
const MIN_QUERY_LEN = 2;

const PaletteContext = createContext({
  open: () => {},
  close: () => {},
  toggle: () => {},
});

export function useAdminCommandPalette() {
  return useContext(PaletteContext);
}

// ─── Provider ─────────────────────────────────────────────────────────────
export function AdminCommandPaletteProvider({ children }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [open, setOpen] = useState(false);

  // Cmd/Ctrl+K global shortcut (only when admin is logged in)
  useEffect(() => {
    if (!isAdmin) return undefined;
    const onKey = (e) => {
      const target = e.target;
      // Don't hijack inside textareas when not toggling
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
      // Avoid usage warning
      return target;
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isAdmin, open]);

  const ctx = useMemo(
    () => ({
      open: () => setOpen(true),
      close: () => setOpen(false),
      toggle: () => setOpen((v) => !v),
    }),
    []
  );

  return (
    <PaletteContext.Provider value={ctx}>
      {children}
      {isAdmin && <AdminCommandPalette open={open} onOpenChange={setOpen} />}
    </PaletteContext.Provider>
  );
}

// ─── Palette Modal ────────────────────────────────────────────────────────
function AdminCommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [groups, setGroups] = useState({ properties: [], projects: [], users: [], leads: [] });
  const [tookMs, setTookMs] = useState(0);
  const [active, setActive] = useState(0);
  const reqIdRef = useRef(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  // Reset state on open
  useEffect(() => {
    if (open) {
      setQ("");
      setGroups({ properties: [], projects: [], users: [], leads: [] });
      setActive(0);
      // Focus input next paint
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // Debounced search
  useEffect(() => {
    if (!open) return undefined;
    if (q.trim().length < MIN_QUERY_LEN) {
      setGroups({ properties: [], projects: [], users: [], leads: [] });
      setBusy(false);
      return undefined;
    }
    setBusy(true);
    const myReq = ++reqIdRef.current;
    const t = setTimeout(async () => {
      try {
        const r = await api.get("/admin/global-search", {
          params: { q: q.trim(), limit: 15 },
        });
        if (myReq !== reqIdRef.current) return; // out-of-order guard
        setGroups(r.data?.groups || { properties: [], projects: [], users: [], leads: [] });
        setTookMs(r.data?.took_ms || 0);
        setActive(0);
      } catch (e) {
        toast.error(apiErrorMsg(e, "Search failed"));
      } finally {
        if (myReq === reqIdRef.current) setBusy(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [q, open]);

  // Flat list with section headers for keyboard nav
  const flat = useMemo(() => {
    const out = [];
    for (const key of ENTITY_ORDER) {
      const arr = groups[key] || [];
      if (arr.length === 0) continue;
      out.push({ type: "header", key });
      for (const item of arr) out.push({ type: "row", key, item });
    }
    return out;
  }, [groups]);

  const rows = useMemo(() => flat.filter((x) => x.type === "row"), [flat]);
  const total = rows.length;

  // Keyboard nav
  const onKeyDown = useCallback((e) => {
    if (total === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => Math.min(total - 1, i + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(0, i - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const row = rows[active];
      if (row) openResult(row.key, row.item);
    }
  }, [active, rows, total]);

  const openResult = useCallback((kind, item) => {
    onOpenChange(false);
    if (kind === "properties") navigate(`/property/${item.id}`);
    else if (kind === "projects") navigate(`/project/${item.id}`);
    else if (kind === "users") navigate(`/admin/users/${item.id}`);
    else if (kind === "leads") navigate(`/admin/crm?lead=${item.id}`);
  }, [navigate, onOpenChange]);

  // Quick actions — mutate state without closing
  const optimisticUpdate = (kind, id, patch) => {
    setGroups((g) => ({
      ...g,
      [kind]: (g[kind] || []).map((it) => it.id === id ? { ...it, ...patch } : it),
    }));
  };

  const verifyProperty = async (item) => {
    try {
      await api.post(`/admin/properties/${item.id}/approve`);
      optimisticUpdate("properties", item.id, { approval_status: "approved", verified_by_house_entry: true });
      toast.success(`Verified: ${item.title || item.id}`);
    } catch (e) {
      toast.error(apiErrorMsg(e, "Verify failed"));
    }
  };
  const toggleFeatured = async (item) => {
    try {
      const next = !item.featured;
      await api.post(`/admin/properties/${item.id}/featured`, { featured: next });
      optimisticUpdate("properties", item.id, { featured: next });
      toast.success(next ? "Marked featured" : "Removed from featured");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Featured toggle failed"));
    }
  };
  const toggleSuspended = async (item) => {
    try {
      const next = !item.blocked;
      await api.patch(`/admin/users/${item.id}`, { blocked: next });
      optimisticUpdate("users", item.id, { blocked: next });
      toast.success(next ? "User suspended" : "User unsuspended");
    } catch (e) {
      toast.error(apiErrorMsg(e, "Suspend toggle failed"));
    }
  };

  let cursor = -1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="!max-w-2xl !p-0 !gap-0 overflow-hidden rounded-2xl border border-slate-200 shadow-2xl [&>button.absolute]:hidden"
        data-testid="admin-command-palette"
        onKeyDown={onKeyDown}
      >
        <VisuallyHidden.Root>
          <DialogTitle>Admin global search</DialogTitle>
          <DialogDescription>
            Search across properties, projects, users, and leads. Use arrow keys to navigate, Enter to open, and Escape to close.
          </DialogDescription>
        </VisuallyHidden.Root>
        {/* Top — search input */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b border-slate-200">
          <Search size={16} className="text-slate-400 shrink-0" />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search properties · projects · users · leads…"
            className="flex-1 bg-transparent text-[14px] outline-none placeholder:text-slate-400 font-sans"
            data-testid="acp-input"
            autoComplete="off"
            spellCheck={false}
          />
          {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
          {/* Clear button — always mounted (visibility-toggled) so a click
              doesn't unmount the element mid-pointerup and trigger Radix's
              outside-click handler (which would close the dialog). */}
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setQ("");
              setTimeout(() => inputRef.current?.focus(), 0);
            }}
            disabled={!q || busy}
            className={`text-slate-400 hover:text-slate-700 transition-opacity ${
              q && !busy ? "opacity-100" : "opacity-0 pointer-events-none"
            }`}
            data-testid="acp-clear"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        <div ref={listRef} className="max-h-[60vh] overflow-y-auto py-1 bg-white">
          {q.trim().length < MIN_QUERY_LEN ? (
            <EmptyHelp />
          ) : total === 0 && !busy ? (
            <div className="px-4 py-10 text-center text-[12.5px] text-slate-500" data-testid="acp-empty">
              No results for <b className="text-slate-700">"{q}"</b>
            </div>
          ) : (
            ENTITY_ORDER.map((key) => {
              const arr = groups[key] || [];
              if (arr.length === 0) return null;
              const Meta = ENTITY_META[key];
              return (
                <div key={key} className="px-1 py-1">
                  <div className="px-3 pt-1.5 pb-1 flex items-center gap-2 text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">
                    <Meta.icon size={10} /> {Meta.label}
                    <span className="ml-auto text-[9.5px] font-medium normal-case tracking-normal">{Meta.hint}</span>
                  </div>
                  {arr.map((item) => {
                    cursor += 1;
                    const isActive = cursor === active;
                    return (
                      <ResultRow
                        key={`${key}-${item.id}`}
                        kind={key}
                        item={item}
                        active={isActive}
                        onClick={() => openResult(key, item)}
                        onVerify={key === "properties" ? () => verifyProperty(item) : null}
                        onFeatured={key === "properties" ? () => toggleFeatured(item) : null}
                        onSuspend={key === "users" ? () => toggleSuspended(item) : null}
                      />
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        {/* Footer — keyboard hints */}
        <div className="px-3 py-2 border-t border-slate-200 bg-slate-50 flex items-center gap-3 text-[10.5px] text-slate-500" data-testid="acp-footer">
          <KbdHint><ArrowUp size={9} /><ArrowDown size={9} /></KbdHint><span>Navigate</span>
          <KbdHint><CornerDownLeft size={9} /></KbdHint><span>Open</span>
          <KbdHint>Esc</KbdHint><span>Close</span>
          <div className="ml-auto inline-flex items-center gap-1">
            {total > 0 && <span className="font-medium text-slate-600">{total} results</span>}
            {total > 0 && tookMs > 0 && <span>· {tookMs}ms</span>}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Result Row ───────────────────────────────────────────────────────────
function ResultRow({ kind, item, active, onClick, onVerify, onFeatured, onSuspend }) {
  const Icon = ENTITY_META[kind]?.icon || Tag;

  let title, subtitle, badges;
  if (kind === "properties") {
    title = item.title || item.id;
    subtitle = `${item.bhk ? `${item.bhk} · ` : ""}${item.area || ""}${item.sub_area ? " · " + item.sub_area : ""}`;
    badges = (
      <>
        {item.featured && <Badge tone="amber" icon={Star}>FEATURED</Badge>}
        {item.verified_by_house_entry && <Badge tone="emerald" icon={BadgeCheck}>VERIFIED</Badge>}
        {item.approval_status === "pending" && <Badge tone="amber">PENDING</Badge>}
        {item.status === "Sold" && <Badge tone="slate">SOLD</Badge>}
      </>
    );
  } else if (kind === "projects") {
    title = item.name || item.id;
    subtitle = `${item.area || ""}${item.sub_area ? " · " + item.sub_area : ""}${item.units_count ? ` · ${item.units_count} units` : ""}`;
    badges = (
      <>
        {item.approval_status === "pending" && <Badge tone="amber">PENDING</Badge>}
        {item.status && <Badge tone="slate">{item.status.replace(/_/g, " ")}</Badge>}
      </>
    );
  } else if (kind === "users") {
    title = item.name;
    subtitle = `${item.email || ""}${item.phone ? " · " + item.phone : ""}`;
    badges = (
      <>
        {item.role && <Badge tone="indigo">{item.role.toUpperCase()}</Badge>}
        {item.blocked && <Badge tone="rose" icon={ShieldOff}>SUSPENDED</Badge>}
        {item.tier && <Badge tone="amber" icon={Crown}>{item.tier.toUpperCase()}</Badge>}
      </>
    );
  } else if (kind === "leads") {
    title = item.name;
    subtitle = `${item.phone || ""}${item.email ? " · " + item.email : ""}${item.lead_kind ? " · " + item.lead_kind : ""}`;
    badges = (
      <>
        {item.lead_kind && <Badge tone="indigo">{item.lead_kind.toUpperCase()}</Badge>}
      </>
    );
  }

  const Avatar = (
    <div className={`shrink-0 w-9 h-9 rounded-lg overflow-hidden flex items-center justify-center ${
      kind === "properties" || kind === "projects"
        ? "bg-slate-100 text-slate-500"
        : kind === "users" ? "bg-indigo-50 text-indigo-700"
        : "bg-amber-50 text-amber-700"
    }`}>
      {(kind === "properties" || kind === "projects") && item.image ? (
        <img src={item.image} alt="" className="w-full h-full object-cover" loading="lazy" decoding="async" />
      ) : (
        <Icon size={14} />
      )}
    </div>
  );

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      data-testid={`acp-row-${kind}-${item.id}`}
      data-active={active ? "true" : "false"}
      className={`mx-1 px-3 py-2 rounded-lg flex items-center gap-3 cursor-pointer transition-colors ${
        active ? "bg-[#0055FF]/10 ring-1 ring-[#0055FF]/30" : "hover:bg-slate-50"
      }`}
    >
      {Avatar}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 min-w-0">
          <div className="font-semibold text-[13px] text-slate-900 truncate">{title}</div>
          <div className="flex flex-wrap items-center gap-1 shrink-0">{badges}</div>
        </div>
        <div className="text-[11.5px] text-slate-500 truncate mt-0.5">{subtitle || "—"}</div>
      </div>
      {/* Quick actions */}
      <div className="hidden sm:flex items-center gap-1 shrink-0 opacity-0 group-hover:opacity-100" style={{ opacity: active ? 1 : 0.7 }}>
        {onVerify && item.approval_status !== "approved" && (
          <QuickActionBtn onClick={onVerify} testid={`acp-verify-${item.id}`} icon={BadgeCheck} label="Verify" tone="emerald" />
        )}
        {onFeatured && (
          <QuickActionBtn onClick={onFeatured} testid={`acp-featured-${item.id}`} icon={Star} label={item.featured ? "Unfeature" : "Feature"} tone={item.featured ? "slate" : "amber"} />
        )}
        {onSuspend && (
          <QuickActionBtn onClick={onSuspend} testid={`acp-suspend-${item.id}`} icon={item.blocked ? ShieldCheck : ShieldOff} label={item.blocked ? "Unsuspend" : "Suspend"} tone={item.blocked ? "emerald" : "rose"} />
        )}
      </div>
    </div>
  );
}

function QuickActionBtn({ onClick, icon: Icon, label, tone = "slate", testid }) {
  const toneCls = {
    emerald: "border-emerald-200 text-emerald-700 hover:bg-emerald-50",
    amber:   "border-amber-200   text-amber-700   hover:bg-amber-50",
    rose:    "border-rose-200    text-rose-700    hover:bg-rose-50",
    slate:   "border-slate-200   text-slate-700   hover:bg-slate-100",
  }[tone];
  return (
    <button
      type="button"
      data-testid={testid}
      onClick={(e) => { e.stopPropagation(); onClick(); }}
      className={`inline-flex items-center gap-1 border bg-white rounded-md px-2 py-1 text-[10.5px] font-semibold transition-colors ${toneCls}`}
    >
      <Icon size={10} /> {label}
    </button>
  );
}

function Badge({ tone, icon: Icon, children }) {
  const map = {
    amber:   "bg-amber-50 text-amber-700 border-amber-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose:    "bg-rose-50 text-rose-700 border-rose-200",
    slate:   "bg-slate-100 text-slate-600 border-slate-200",
    indigo:  "bg-indigo-50 text-indigo-700 border-indigo-200",
  };
  return (
    <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[9px] uppercase tracking-wide font-bold rounded border ${map[tone] || map.slate}`}>
      {Icon && <Icon size={8} />} {children}
    </span>
  );
}

function KbdHint({ children }) {
  return (
    <kbd className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-mono text-slate-600">
      {children}
    </kbd>
  );
}

// ─── Empty/help state when no query ───────────────────────────────────────
function EmptyHelp() {
  return (
    <div className="p-5 text-[12px] text-slate-500" data-testid="acp-help">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400 mb-2">Search across</div>
      <div className="grid grid-cols-2 gap-2">
        {ENTITY_ORDER.map((k) => {
          const M = ENTITY_META[k];
          return (
            <div key={k} className="bg-slate-50 border border-slate-200 rounded-lg p-2.5">
              <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-700">
                <M.icon size={11} /> {M.label}
              </div>
              <div className="text-[10.5px] text-slate-500 mt-0.5">{M.hint}</div>
            </div>
          );
        })}
      </div>
      <div className="mt-3 text-[10.5px] text-slate-400 flex flex-wrap items-center gap-1">
        <Sparkles size={10} /> Tip: type a phone, property id, or "2BHK" — the search auto-detects.
      </div>
    </div>
  );
}

// ─── Trigger button (mobile + admin header) ───────────────────────────────
export function AdminCommandPaletteTrigger({ className = "", iconOnly = false, testid = "admin-cmdk-trigger" }) {
  const { user } = useAuth();
  const { open } = useAdminCommandPalette();
  if (user?.role !== "admin") return null;
  return (
    <button
      type="button"
      onClick={open}
      data-testid={testid}
      className={
        className ||
        "inline-flex items-center gap-2 bg-white border border-slate-200 hover:border-slate-300 rounded-lg px-2.5 py-1.5 text-[12px] text-slate-500 transition-colors"
      }
    >
      <Search size={13} />
      {!iconOnly && (
        <>
          <span className="hidden sm:inline">Search</span>
          <kbd className="hidden sm:inline-flex items-center gap-0.5 ml-2 px-1.5 py-0.5 rounded border border-slate-200 bg-slate-50 text-[9.5px] font-mono text-slate-500">
            ⌘K
          </kbd>
        </>
      )}
    </button>
  );
}

// ─── Mobile FAB ───────────────────────────────────────────────────────────
export function AdminCommandPaletteFAB() {
  const { user } = useAuth();
  const { open } = useAdminCommandPalette();
  if (user?.role !== "admin") return null;
  return (
    <button
      type="button"
      onClick={open}
      data-testid="admin-cmdk-fab"
      className="sm:hidden fixed bottom-20 right-4 z-40 w-12 h-12 rounded-full bg-[#0055FF] text-white shadow-lg shadow-blue-500/30 flex items-center justify-center hover:bg-[#0044CC] active:scale-95 transition-all"
      aria-label="Open admin search"
    >
      <Search size={18} />
    </button>
  );
}
